import {
  Bookmark,
  Box,
  BrainCircuit,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Crown,
  Eye,
  Flame,
  GitBranch,
  Languages,
  LifeBuoy,
  Mail,
  Shield,
  Star,
  UserRound,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getRecentRepoViews, getUserExploration, getUserToolbox, getVipMe } from '../api.js'
import { useAuth } from '../auth-context.js'
import MembershipStatusCard from '../components/MembershipStatusCard.jsx'
import { NOINDEX_ROBOTS, setPageMetadata } from '../page-metadata.js'

function formatDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 19)
  return new Intl.DateTimeFormat('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function formatNumber(value) {
  return new Intl.NumberFormat('zh-CN').format(value || 0)
}

function daysLeft(value) {
  if (!value) return 0
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 0
  return Math.max(0, Math.ceil((date.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
}

function planLabel(value) {
  const text = String(value || '').trim()
  if (text === 'monthly') return '月卡VIP'
  if (text === 'quarterly') return '季卡VIP'
  if (text === 'yearly') return '年卡VIP'
  if (!text) return '普通用户'
  return text.includes('VIP') ? text : `${text}VIP`
}

function ProfilePage() {
  const { user } = useAuth()
  const isAdmin = user?.isAdmin === true || user?.is_admin === true
  const [activity, setActivity] = useState(null)
  const [activityError, setActivityError] = useState(false)
  const [toolbox, setToolbox] = useState(null)
  const [exploration, setExploration] = useState(null)
  const [vipMe, setVipMe] = useState(null)

  useEffect(() => setPageMetadata({
    title: '个人中心 - 青途 AI',
    description: '青途 AI 个人中心。',
    robots: NOINDEX_ROBOTS,
  }), [])

  useEffect(() => {
    const controller = new AbortController()
    getRecentRepoViews(controller.signal)
      .then(setActivity)
      .catch((error) => {
        if (error.name !== 'AbortError') setActivityError(true)
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([
      getUserToolbox(controller.signal).catch(() => null),
      getUserExploration(controller.signal).catch(() => null),
      getVipMe(controller.signal).catch(() => null),
    ]).then(([toolboxResult, explorationResult, vipResult]) => {
      setToolbox(toolboxResult)
      setExploration(explorationResult)
      setVipMe(vipResult)
    })
    return () => controller.abort()
  }, [])

  const roleLabel = isAdmin ? '管理员' : '普通用户'
  const membership = user?.membership || {}
  const membershipTier = membership.tier || 'free'
  const membershipLabel = membershipTier === 'admin'
    ? 'Admin'
    : membershipTier === 'vip'
      ? 'VIP'
      : 'Free'
  const registeredAt = formatDate(user.createdAt)
  const stats = activity?.stats
  const recentViews = activity?.items || []
  const toolboxGroups = toolbox?.groups || []
  const explorationStats = exploration?.stats
  const vipOrders = vipMe?.orders || []
  const currentVipMembership = vipMe?.active ? vipMe?.membership : null

  return (
    <div className="page-container profile-page">
      <section className="page-heading compact-heading profile-heading">
        <div>
          <span className="eyebrow">YOUR PROFILE</span>
          <h1>我的主页</h1>
          <p>查看账号信息、浏览足迹，并快速回到你关注的开源项目。</p>
        </div>
        <span className={`profile-role-badge ${isAdmin ? 'is-admin' : ''}`}>
          {isAdmin && <Shield size={14} />}
          {roleLabel}
        </span>
      </section>
      <MembershipStatusCard membershipOverride={currentVipMembership} />

      <section className="profile-grid">
        <article className="profile-card profile-account-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><UserRound size={20} /></span>
            <div>
              <h2>账号信息</h2>
              <p>当前登录账号的公开资料</p>
            </div>
          </header>
          <dl className="profile-account-list">
            <div>
              <dt><UserRound size={15} /> 用户名</dt>
              <dd>{user.username}</dd>
            </div>
            <div>
              <dt><Mail size={15} /> 邮箱</dt>
              <dd>{user.email || '未填写'}</dd>
            </div>
            <div>
              <dt><GitBranch size={15} /> 登录方式</dt>
              <dd>{user.provider === 'github' ? 'GitHub' : '本地账号'}</dd>
            </div>
            <div>
              <dt><CheckCircle2 size={15} /> 角色</dt>
              <dd>{user.role || (isAdmin ? 'admin' : 'user')}</dd>
            </div>
            {registeredAt && (
              <div>
                <dt><CalendarDays size={15} /> 注册时间</dt>
                <dd>{registeredAt}</dd>
              </div>
            )}
          </dl>
          {user.provider === 'github' && (
            <p className="profile-muted">
              GitHub 账号没有本地密码，请前往 GitHub 修改密码。
            </p>
          )}
        </article>

        <article className="profile-card profile-action-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><Eye size={20} /></span>
            <div>
              <h2>我的浏览量</h2>
              <p>已计入的有效项目访问次数</p>
            </div>
          </header>
          <strong className="profile-stat">
            {activityError ? '--' : stats ? formatNumber(stats.totalViews) : '…'}
            <small> 次浏览</small>
          </strong>
          <p className="profile-card-copy">
            已访问 {stats ? formatNumber(stats.visitedRepoCount) : '…'} 个不同项目
          </p>
        </article>

        <article className="profile-card profile-action-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><Crown size={20} /></span>
            <div>
              <h2>VIP</h2>
              <p>会员状态和每日功能额度</p>
            </div>
          </header>
          <strong className="profile-stat">
            {currentVipMembership ? planLabel(currentVipMembership.planName || currentVipMembership.planId) : membershipLabel}
          </strong>
          {(currentVipMembership?.expiresAt || membership.expiresAt) && (
            <p className="profile-card-copy">
              有效期：{currentVipMembership?.startsAt ? `${formatDate(currentVipMembership.startsAt)} 至 ` : ''}{formatDate(currentVipMembership?.expiresAt || membership.expiresAt)}
              <br />
              剩余 {daysLeft(currentVipMembership?.expiresAt || membership.expiresAt)} 天
            </p>
          )}
          <Link className="secondary-button profile-card-button" to="/vip">
            <Crown size={16} /> 查看 VIP
          </Link>
        </article>

        <article className="profile-card profile-vip-orders-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><Crown size={20} /></span>
            <div>
              <h2>我的 VIP 订单</h2>
              <p>查看付款证明、审核状态和退款入口。</p>
            </div>
          </header>
          {vipOrders.length === 0 ? (
            <p className="profile-empty-state">暂无 VIP 订单。</p>
          ) : (
            <div className="profile-order-list">
              {vipOrders.slice(0, 5).map((order) => (
                <Link to={`/vip/orders/${order.orderNo}`} key={order.orderNo}>
                  <span>
                    <strong>{order.planName || order.planId}</strong>
                    <small>{order.orderNo}</small>
                  </span>
                  <em>¥{order.amountYuan}</em>
                  <small>{order.status}</small>
                </Link>
              ))}
            </div>
          )}
        </article>

        <article className="profile-card profile-action-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><LifeBuoy size={20} /></span>
            <div>
              <h2>客服与反馈</h2>
              <p>提交开通失败、重复付款、退款申请、功能异常和额度问题。</p>
            </div>
          </header>
          <strong className="profile-stat">Support</strong>
          <Link className="secondary-button profile-card-button" to="/support">
            <LifeBuoy size={16} /> 提交工单
          </Link>
        </article>

        <article className="profile-card profile-action-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><Bookmark size={20} /></span>
            <div>
              <h2>我的收藏</h2>
              <p>继续研究你保存过的项目</p>
            </div>
          </header>
          <strong className="profile-stat">
            {activityError ? '--' : stats ? formatNumber(stats.favoriteCount) : '…'}
            <small> 个项目</small>
          </strong>
          <Link className="secondary-button profile-card-button" to="/favorites">
            <Bookmark size={16} /> 查看我的收藏
          </Link>
        </article>

        <article className="profile-card profile-toolbox-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><Box size={20} /></span>
            <div>
              <h2>我的工具箱</h2>
              <p>把收藏升级成真正会回来的工具清单</p>
            </div>
          </header>
          <strong className="profile-stat">
            {toolbox ? formatNumber(toolbox.total) : '…'}
            <small> 个工具</small>
          </strong>
          <div className="profile-toolbox-groups">
            {toolboxGroups.slice(0, 5).map((group) => (
              <span key={group.key}>
                {group.label}<strong>{formatNumber(group.items?.length || 0)}</strong>
              </span>
            ))}
          </div>
          <div className="profile-toolbox-status">
            <span>收藏 {formatNumber(toolbox?.statusCounts?.favorite || 0)}</span>
            <span>待尝试 {formatNumber(toolbox?.statusCounts?.to_try || 0)}</span>
            <span>常用 {formatNumber(toolbox?.statusCounts?.frequent || 0)}</span>
            <span>已使用 {formatNumber(toolbox?.statusCounts?.used || 0)}</span>
          </div>
        </article>

        <article className="profile-card profile-action-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><Flame size={20} /></span>
            <div>
              <h2>连续探索</h2>
              <p>保持轻量记录，不做打卡压力</p>
            </div>
          </header>
          <div className="profile-exploration-grid">
            <div><strong>{explorationStats?.streakDays ?? '…'}</strong><small>连续天数</small></div>
            <div><strong>{explorationStats?.weeklyDiscoveredTools ?? '…'}</strong><small>本周发现</small></div>
            <div><strong>{explorationStats?.weeklyToolboxAdds ?? '…'}</strong><small>本周收藏</small></div>
            <div><strong>{explorationStats?.toolboxCompletion ?? '…'}%</strong><small>完善度</small></div>
          </div>
        </article>

        <article className="profile-card profile-behavior-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><Flame size={20} /></span>
            <div>
              <h2>浏览偏好</h2>
              <p>根据你的有效浏览记录生成</p>
            </div>
          </header>
          <div className="profile-behavior-grid">
            <div>
              <small>访问过的项目</small>
              <strong>{stats ? formatNumber(stats.visitedRepoCount) : '…'}</strong>
            </div>
            <div>
              <small>最常访问</small>
              {stats?.mostVisitedRepo ? (
                <Link to={`/repos/${stats.mostVisitedRepo.repoId}`}>
                  {stats.mostVisitedRepo.fullName}
                </Link>
              ) : (
                <strong>暂无</strong>
              )}
            </div>
            <div>
              <small>浏览最多的语言</small>
              <strong>
                <Languages size={15} />
                {stats?.topLanguage?.language || '暂无'}
              </strong>
            </div>
          </div>
        </article>

        <article className="profile-card profile-recent-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><Clock3 size={20} /></span>
            <div>
              <h2>最近浏览</h2>
              <p>点击项目可继续查看详情</p>
            </div>
          </header>
          {activityError ? (
            <p className="profile-empty-state">最近浏览暂时无法读取。</p>
          ) : activity && recentViews.length === 0 ? (
            <p className="profile-empty-state">还没有浏览记录，去榜单发现一个项目吧。</p>
          ) : (
            <div className="profile-recent-list">
              {recentViews.map((item) => (
                <Link to={`/repos/${item.repoId}`} key={item.repoId}>
                  <div>
                    <strong>{item.fullName}</strong>
                    <small>{formatDateTime(item.viewedAt)}</small>
                  </div>
                  <span><Star size={14} /> {formatNumber(item.stars)}</span>
                  <span><Flame size={14} /> {item.heatScore}</span>
                  <span><Eye size={14} /> {formatNumber(item.viewCount)}</span>
                </Link>
              ))}
            </div>
          )}
        </article>

        <article className="profile-card profile-action-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><BrainCircuit size={20} /></span>
            <div>
              <h2>我的 GitHub 分析</h2>
              <p>判断公开项目的学习与简历价值</p>
            </div>
          </header>
          <p className="profile-card-copy">
            选择你的公开仓库，获取项目优缺点、优化路线和面试表达建议。
          </p>
          <Link className="secondary-button profile-card-button" to="/my-github">
            <GitBranch size={16} /> 前往我的 GitHub
          </Link>
        </article>

        <article className="profile-card profile-tips-card">
          <header className="profile-card-heading">
            <span className="profile-card-icon"><CheckCircle2 size={20} /></span>
            <div>
              <h2>使用建议</h2>
              <p>更快找到真正值得投入的开源项目</p>
            </div>
          </header>
          <ol className="profile-tip-list">
            <li>从每日、每周和总榜发现项目</li>
            <li>浏览详情并结合热度指标判断活跃度</li>
            <li>收藏值得持续研究的开源项目</li>
          </ol>
        </article>

        {isAdmin && (
          <article className="profile-card profile-admin-card">
            <header className="profile-card-heading">
              <span className="profile-card-icon"><Shield size={20} /></span>
              <div>
                <h2>管理员快捷入口</h2>
                <p>查看榜单任务、用户和系统运行状态</p>
              </div>
            </header>
            <Link className="primary-button profile-card-button" to="/admin">
              <Shield size={16} /> 进入后台
            </Link>
          </article>
        )}
      </section>
    </div>
  )
}

export default ProfilePage
